// CC0 sound library (sfx/music/jingles) served from the public common blob.
// Uploaded by aidea-middleware scripts/upload-sound-library.ts — keep prefix in sync.
import i18n from "i18next";

const ROOT_URL =
  (process.env.REACT_APP_LIBRARY_ROOT_URL ||
    "https://cloud.aideapro.ai/middlewareblob/common/aideapro/") + "library/sounds/";

let cachePromise = null;

export function fetchSoundLibrary() {
  if (!cachePromise) {
    cachePromise = fetch(ROOT_URL + "manifest.json")
      .then((res) => {
        if (!res.ok) throw new Error("sound library manifest: HTTP " + res.status);
        return res.json();
      })
      .then((items) =>
        items.map((it) => ({
          ...it,
          url: ROOT_URL + it.path,
          displayName: it.filename.replace(/\.mp3$/, "").replace(/-/g, " "),
        }))
      )
      .catch((err) => {
        cachePromise = null; // allow retry on next panel open
        throw err;
      });
  }
  return cachePromise;
}

// Convention: short < 5s, medium is 5s–30s inclusive on both ends, long > 30s.
const DURATION_BUCKETS = {
  short: (sec) => sec < 5,
  medium: (sec) => sec >= 5 && sec <= 30,
  long: (sec) => sec > 30,
};

export function filterSounds(items, { type, search, category, duration }) {
  const q = (search || "").trim().toLowerCase();
  const durationCheck = duration && DURATION_BUCKETS[duration];
  return items.filter(
    (it) =>
      (!type || it.type === type) &&
      (!category || it.category === category) &&
      (!durationCheck || durationCheck(it.durationSec)) &&
      (!q ||
        it.displayName.toLowerCase().includes(q) ||
        (it.nameTr || "").toLowerCase().includes(q) ||
        (it.category || "").toLowerCase().includes(q))
  );
}

// Manifest entries may carry an optional nameTr; fall back to displayName
// when absent or when the UI isn't in Turkish.
export function getDisplayName(item, lang = i18n.language) {
  if (String(lang || "").startsWith("tr") && item.nameTr) return item.nameTr;
  return item.displayName;
}

// Manifest categories (verified live against manifest.json). Fallback for any
// future category not listed here is a prettified kebab-case string.
export const CATEGORY_LABELS = {
  "ui-click": { en: "UI Clicks", tr: "Tıklama Sesleri" },
  notification: { en: "Notifications", tr: "Bildirimler" },
  success: { en: "Success", tr: "Başarı" },
  error: { en: "Error", tr: "Hata" },
  pop: { en: "Pop", tr: "Pop" },
  coin: { en: "Coin", tr: "Madeni Para" },
  reward: { en: "Reward", tr: "Ödül" },
  whoosh: { en: "Whoosh", tr: "Vınlama" },
  impact: { en: "Impact", tr: "Çarpma" },
  door: { en: "Door", tr: "Kapı" },
  thunder: { en: "Thunder", tr: "Gök Gürültüsü" },
  machinery: { en: "Machinery", tr: "Makine" },
  ambience: { en: "Ambience", tr: "Ortam Sesi" },
  "calm-focus": { en: "Calm Focus", tr: "Sakin Odak" },
  "corporate-upbeat": { en: "Corporate Upbeat", tr: "Kurumsal Enerjik" },
  ambient: { en: "Ambient", tr: "Ambiyans" },
  acoustic: { en: "Acoustic", tr: "Akustik" },
  inspiring: { en: "Inspiring", tr: "İlham Verici" },
  "lo-fi": { en: "Lo-Fi", tr: "Lo-Fi" },
  "jingles/stingers": { en: "Jingles", tr: "Cıngıllar" },
  "my-effects": { en: "My Effects", tr: "Efektlerim" },
};

function prettifyCategory(category) {
  return category
    .split(/[\s-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function getCategoryLabel(category, lang = i18n.language) {
  const entry = CATEGORY_LABELS[category];
  if (entry) return String(lang || "").startsWith("tr") ? entry.tr : entry.en;
  return prettifyCategory(category || "");
}
